import type { BookAccount } from '../api/types';

// The collision warning: somebody other than the owner is already working this account.
//
// **The count and the name are different facts and the sentence has to keep them apart.** This
// used to read "<b>Taylor Fang</b> has touched this account 51 times", which credited one person
// with everybody's work -- on one account those 51 touches are four people, and Taylor was not
// even the most recent of them, only the alphabetically last name the old `max(author)`
// returned. The plural form therefore leads with how many people and names the latest one as the
// latest one; the singular form is the only case where one name may carry the whole count.
//
// The subject is deliberately not "we". The number excludes the owner's own touches, so a
// strategist
// reading "we have touched this 51 times" on their own board would count their 8 inside it.
function CollisionNote({ row }: { row: BookAccount }) {
  if (!row.other_touches) return null;
  const touchCount = `${row.other_touches} time${row.other_touches === 1 ? '' : 's'}`;
  const when = row.other_last_touch ? ` on ${row.other_last_touch}` : '';

  return (
    <div className="collide">
      {row.other_authors > 1 ? (
        <>
          <b>{row.other_authors} other people</b> have touched this account {touchCount}, most
          recently <b>{row.other_last_author || 'somebody'}</b>{when}
        </>
      ) : (
        <>
          <b>{row.other_last_author || 'Somebody else'}</b> has touched this account {touchCount},
          most recently{when || ' recently'}
        </>
      )}
      .
    </div>
  );
}

// Preserve the colleague's words. A note is context, not an inferred permission rule.
export function OutreachContext({ row }: { row: BookAccount }) {
  const note = row.clay?.human_notes?.trim();
  if (!note && !row.other_touches) return null;

  return (
    <section className="outreachcontext" aria-label="Team outreach context">
      <h3>Before you reach out</h3>
      {note ? <p className="prose">{note}</p> : null}
      <CollisionNote row={row} />
    </section>
  );
}
